import React, { useState } from 'react'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { User, UserProps } from '@/entities/user'
import Loader from '@/components/ui_custom/loader'
import { useLoader } from '@/contexts/LoaderContext'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/store/auth'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { Input } from '@/components/ui_custom/input'
import { Button } from '@/components/ui_custom/button'
import { formatDate } from '@/utils/dashboard-dates-util'
import { HotmartData } from '@/components/common/hotmart-data'
// import { joinNonEmpty } from '@/utils/string-util'

// types
type UserHotmartDataDialogProps = {
  open: boolean
  cb: () => void
}

// variables
const loc = 'components/dialogs/user-hotmart-data-dialog'

function Spinner({ className = '' }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
    </svg>
  )
}

export function UserHotmartDataDialog({ open, cb }: UserHotmartDataDialogProps) {
  // const [isOpen, setIsOpen] = useState<boolean>(open)
  const { user } = useAuthStore()
  const [hotmartData, setHotmartData] = useState<unknown | null>(null)
  const [userEmail, setUserEmail] = useState<string>('')
  const { showLoader, hideLoader, isLoading } = useLoader()
  const { toast } = useToast()
  const _handleCancel = async () => {
    // setIsOpen(false)
    cb()
  }

  const _handleSearchUserHotmartData = async () => {
    try {
      showLoader()

      const response = await api.get('/', {
        params: {
          action: 'user-hotmart-data',
          email: userEmail?.trim()?.toLowerCase() || '',
          userRef: user?.ref
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setHotmartData(response.data.body[0] || null)
        if (!response.data.body[0]?.sale && !response.data.body[0]?.subscription) {
          toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi encontrado dados na Hotmart.' })
        }
      } else {
        toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível ver os detalhes do usuário.' })
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível ver os detalhes do usuário.' })
      console.error(`Unhandled error at ${loc}._handleShowUserDetails. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  return (
    <CustomDialog open={open} persistent={true} title="Detalhes do usuário na Hotmart" confirmationBeforeClose={false} onClose={_handleCancel} className="min-w-screen">
      <div className="w-full max-w-4xl mx-auto">
        <div className="group relative mb-6">
          <div className="flex gap-2 p-1.5 rounded-2xl bg-white border border-gray-200 shadow-sm transition hover:shadow-md">
            <div className="relative flex-1">
              <Input
                type="email"
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                placeholder="Digite o email"
                disabled={isLoading}
                onKeyDown={e => {
                  if (e.key === 'Enter') _handleSearchUserHotmartData?.()
                }}
                className="w-full rounded-xl border border-transparent focus:border-pink-300 focus:ring-4 focus:ring-pink-100 outline-none px-4 py-3 text-sm placeholder:text-gray-400"
              />
            </div>
            <Button
              onClick={_handleSearchUserHotmartData}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-fuchsia-600 px-4 py-2.5 text-white text-sm font-medium shadow-sm transition active:scale-[.98] disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Buscar dados no Hotmart"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4" /> Buscando…
                </span>
              ) : (
                <span className="flex items-center gap-2">Buscar</span>
              )}
            </Button>
          </div>
          {isLoading && (
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full w-1/3 animate-[loading_1.2s_ease_infinite] rounded-full bg-gradient-to-r from-pink-400 to-fuchsia-500" />
            </div>
          )}
        </div>

        <HotmartData hotmartData={hotmartData} isLoading={isLoading} />

        <style>{`
          @keyframes loading {
          0% { transform: translateX(-100%);}
          50% { transform: translateX(30%);}
          100% { transform: translateX(120%);}
          }
        `}</style>
      </div>
    </CustomDialog>
  )
}
